// Global state to track active drawers
const activeDrawers = new Set<Ref<boolean>>()
let popstateHandler: ((event: PopStateEvent) => void) | null = null
// Flag to track when we're canceling a back navigation (to ignore the popstate from history.forward())
let isCancelingBackNavigation = false
// Stored scroll position for drawer history navigation (used by router.options.ts)
let savedScrollPosition: number | null = null

// Getter function for router.options.ts to get saved scroll position
export function getSavedScrollPosition() {
    const position = savedScrollPosition
    // Clear after reading
    savedScrollPosition = null
    return position
}

/**
 * Global popstate handler that intercepts back button/swipe back
 * When a drawer is open, closes it instead of navigating
 */
function setupGlobalPopstateHandler() {
    if (typeof window === 'undefined' || popstateHandler) return

    popstateHandler = (_event: PopStateEvent) => {
        // If we're canceling a back navigation, ignore this popstate event (it's from history.forward())
        if (isCancelingBackNavigation) {
            isCancelingBackNavigation = false
            return
        }

        // Find any open drawer
        let openDrawer: Ref<boolean> | null = null
        for (const drawer of activeDrawers) {
            if (drawer.value) {
                openDrawer = drawer
                break
            }
        }

        if (openDrawer) {
            // Save scroll position for Nuxt's scrollBehavior to use
            savedScrollPosition = window.scrollY

            // Close the drawer
            openDrawer.value = false

            // Mark that we're canceling the back navigation
            isCancelingBackNavigation = true

            // Go forward to cancel the back navigation and stay on current page
            // This will trigger another popstate event, but we'll ignore it
            window.history.forward()
        }
    }

    window.addEventListener('popstate', popstateHandler)
}

/**
 * Clean up global popstate handler when no drawers are active
 */
function cleanupGlobalPopstateHandler() {
    if (typeof window === 'undefined' || !popstateHandler) return

    if (activeDrawers.size === 0) {
        window.removeEventListener('popstate', popstateHandler)
        popstateHandler = null
    }
}

/**
 * Composable to intercept back button/swipe back when drawer is open.
 * When the back button is pressed, it closes the drawer instead of navigating.
 *
 * @param isOpen - Reactive ref that controls drawer open state
 */
export function useDrawerHistory(isOpen: Ref<boolean>) {
    // Register this drawer in the global state
    activeDrawers.add(isOpen)

    // Set up global popstate handler on mount
    onMounted(() => {
        setupGlobalPopstateHandler()
    })

    // Clean up when component unmounts
    onUnmounted(() => {
        activeDrawers.delete(isOpen)
        cleanupGlobalPopstateHandler()
    })

    return {}
}
